import { useEffect, useMemo, useState } from 'react';
import { getFrequentPanditSuggestion } from '../utils/bookingPreferences';
import catalogService from '../services/catalogService';
import availabilityService from '../services/availabilityService';
import authService from '../services/authService';
import { resolveServicePricingConfig } from '../config/servicePricingConfig';
import { normalizeService } from '../config/catalog/normalizedServiceSchema';

const normalizeText = (value) => String(value || '').toLowerCase();

const toNumberPrice = (value) => {
  if (typeof value === 'number') {
    return value;
  }
  const parsed = parseInt(String(value || '').replace(/[^\d]/g, ''), 10);
  return Number.isFinite(parsed) ? parsed : 0;
};

const roundToNearestHundred = (value) => Math.round(value / 100) * 100;

const resolveServicePage = (service = {}) => {
  const hints = String(service.servicePage || service.category || service.title || service.name || service.id || '').toLowerCase();

  if (hints in SERVICE_PAGE_LABELS) {
    return hints;
  }

  if (/wedding|engagement|mehendi|marriage/.test(hints)) return 'wedding-services';
  if (/home|griha|vastu|ganpati|lakshmi/.test(hints)) return 'home-pooja-services';
  if (/festival|diwali|navratri|dussehra|karva/.test(hints)) return 'festival-services';
  if (/life|mundan|annaprashan|naming|thread/.test(hints)) return 'life-events';
  if (/spiritual|katha|hanuman|bhagwat|rudra|satyanarayan/.test(hints)) return 'spiritual-services';
  if (/special|birthday|anniversary|havan/.test(hints)) return 'special-occasions';

  return 'home';
};

const SERVICE_PAGE_LABELS = {
  'wedding-services': 'Wedding Services',
  'home-pooja-services': 'Home Pooja Services',
  'festival-services': 'Festival Services',
  'life-events': 'Life Events',
  'spiritual-services': 'Spiritual Services',
  'special-occasions': 'Special Occasions',
  'most-booked-services': 'Most Booked Services',
  'new-services': 'New Services',
  home: 'Services'
};


const SERVICE_PROFILES = [
  {
    key: 'wedding',
    matchers: ['wedding', 'engagement', 'ring', 'mehendi', 'sangam'],
    subtitle: 'Tailored ceremonial planning with complete wedding ritual support.',
    premiumLabel: 'Celebration Plus',
    luxuryLabel: 'Royal Wedding',
    addOns: [
      { id: 'photo', name: 'Event Photography', factor: 0.3, min: 7000, description: 'Professional photography with edited highlights.' },
      { id: 'video', name: 'Event Videography', factor: 0.38, min: 9000, description: 'Cinematic ceremony film with key rituals captured.' },
      { id: 'decor', name: 'Traditional Decor Setup', factor: 0.25, min: 6000, description: 'Theme-based decor matching your ceremony style.' },
      { id: 'music', name: 'Live Shehnai & Mantras', factor: 0.18, min: 4500, description: 'Live devotional ambience with curated ritual chants.' }
    ]
  },
  {
    key: 'festival',
    matchers: ['diwali', 'navratri', 'karva', 'dussehra', 'janmashtami', 'holi', 'festival'],
    subtitle: 'Festival-focused rituals with community and family participation.',
    premiumLabel: 'Festival Grand',
    luxuryLabel: 'Festival Elite',
    addOns: [
      { id: 'rangoli', name: 'Rangoli & Decor Kit', factor: 0.14, min: 2500, description: 'Handcrafted festive rangoli and decoration package.' },
      { id: 'bhajan', name: 'Bhajan Team Support', factor: 0.22, min: 4200, description: 'Live devotional singing team for the event.' },
      { id: 'prasad', name: 'Prasad Preparation', factor: 0.16, min: 2800, description: 'Freshly prepared prasad with hygiene assurance.' },
      { id: 'festival-stream', name: 'Live Streaming Setup', factor: 0.2, min: 3500, description: 'Share festival moments with family remotely.' }
    ]
  },
  {
    key: 'home',
    matchers: ['griha', 'vastu', 'home', 'lakshmi', 'ganpati', 'rudrabhishek', 'pooja'],
    subtitle: 'Home-centric pooja setup with vastu and tradition-aligned guidance.',
    premiumLabel: 'Home Harmony Plus',
    luxuryLabel: 'Divine Home Elite',
    addOns: [
      { id: 'samagri', name: 'Complete Pooja Samagri', factor: 0.18, min: 2200, description: 'All ritual materials sourced and delivered at home.' },
      { id: 'vastu-consult', name: 'Vastu Consultation', factor: 0.24, min: 3500, description: 'Practical vastu suggestions during ceremony visit.' },
      { id: 'sankalp', name: 'Extended Sankalp Path', factor: 0.12, min: 1800, description: 'Long-form sankalp recitation for family members.' },
      { id: 'prasadam', name: 'Special Prasadam Pack', factor: 0.1, min: 1600, description: 'Blessed prasadam prepared for all participants.' }
    ]
  },
  {
    key: 'life',
    matchers: ['mundan', 'annaprashan', 'naming', 'thread', 'life'],
    subtitle: 'Life milestone ceremonies designed around family traditions.',
    premiumLabel: 'Family Blessings Plus',
    luxuryLabel: 'Milestone Signature',
    addOns: [
      { id: 'family-ritual-kit', name: 'Family Ritual Kit', factor: 0.15, min: 2000, description: 'Extra ritual materials for extended family participation.' },
      { id: 'memory-capture', name: 'Memory Capture', factor: 0.2, min: 3200, description: 'Candid coverage of important life-moment rituals.' },
      { id: 'child-comfort', name: 'Child Comfort Setup', factor: 0.1, min: 1500, description: 'Comfort-focused arrangements for child ceremonies.' },
      { id: 'blessing-cards', name: 'Blessing Cards', factor: 0.08, min: 1200, description: 'Personalized family blessing cards for keepsake.' }
    ]
  },
  {
    key: 'spiritual',
    matchers: ['spiritual', 'katha', 'hanuman', 'bhagwat', 'satyanarayan', 'rudra'],
    subtitle: 'Deep spiritual guidance with authentic recitation and ritual flow.',
    premiumLabel: 'Spiritual Deep Dive',
    luxuryLabel: 'Anushthan Signature',
    addOns: [
      { id: 'extended-path', name: 'Extended Path Session', factor: 0.22, min: 3000, description: 'Additional recitation session for deeper spiritual practice.' },
      { id: 'group-guidance', name: 'Group Guidance Circle', factor: 0.16, min: 2500, description: 'Interactive Q&A with pandit after main ritual.' },
      { id: 'sacred-kit', name: 'Sacred Kit', factor: 0.14, min: 2200, description: 'Curated sacred items for continued daily worship.' },
      { id: 'audio-mantras', name: 'Custom Mantra Audio', factor: 0.1, min: 1700, description: 'Recorded mantra guide personalized for your family.' }
    ]
  }
];

const DEFAULT_SERVICE_PROFILE = {
  key: 'general',
  subtitle: 'Personalized planning and ritual execution based on your selected service.',
  premiumLabel: 'Enhanced Package',
  luxuryLabel: 'Signature Package',
  addOns: [
    { id: 'priority-support', name: 'Priority Coordination', factor: 0.15, min: 2000, description: 'Dedicated coordinator for ceremony planning and updates.' },
    { id: 'material-kit', name: 'Material Kit', factor: 0.12, min: 1800, description: 'Essential ceremony materials arranged on your behalf.' },
    { id: 'family-briefing', name: 'Family Briefing Session', factor: 0.09, min: 1400, description: 'Pre-ceremony orientation for all participants.' }
  ]
};

const resolveServiceProfile = (service) => {
  const title = normalizeText(`${service?.title || ''} ${service?.name || ''}`);
  return SERVICE_PROFILES.find((profile) => profile.matchers.some((matcher) => title.includes(matcher))) || DEFAULT_SERVICE_PROFILE;
};

const getProviderId = (pandit) => Number(pandit?.userId || pandit?.id || 0) || null;

const defaultService = normalizeService({}, {
  title: 'Service Details',
  name: 'Service Details',
  description: '',
  features: [],
  image: '🕉️'
});

const useServiceDetailsModel = ({ service, onNavigate }) => {
  const [selectedPackage, setSelectedPackage] = useState('basic');
  const [selectedAddOns, setSelectedAddOns] = useState([]);
  const [catalogPandits, setCatalogPandits] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [availabilityActorIds, setAvailabilityActorIds] = useState(new Set());
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [cityFilter, setCityFilter] = useState('');
  const [onlyAvailable, setOnlyAvailable] = useState(false);
  const [sortBy, setSortBy] = useState('best');
  const [dakshinaAmount, setDakshinaAmount] = useState(0);
  const [selectedDurationDays, setSelectedDurationDays] = useState(1);
  const [selectedPanditCount, setSelectedPanditCount] = useState(1);
  const [selectedJaapCount, setSelectedJaapCount] = useState(0);
  const [selectedAnusthanType, setSelectedAnusthanType] = useState('');

  const serviceData = normalizeService(service, defaultService);
  const parentPage = useMemo(() => {
    return service?.servicePage || resolveServicePage(serviceData);
  }, [service, serviceData]);
  const parentLabel = useMemo(() => {
    return SERVICE_PAGE_LABELS[parentPage] || 'Services';
  }, [parentPage]);
  const serviceProfile = useMemo(() => resolveServiceProfile(serviceData), [serviceData]);
  const servicePricingConfig = useMemo(() => resolveServicePricingConfig(serviceData), [serviceData]);
  const schedulePricing = servicePricingConfig?.schedulePricing || null;
  const jaapPricing = servicePricingConfig?.jaapPricing || null;
  const anusthanPricing = servicePricingConfig?.anusthanPricing || null;
  const frequentSuggestion = getFrequentPanditSuggestion(serviceData.title);
  const providerCandidates = catalogPandits;

  const suggestedPandit = useMemo(() => {
    if (!frequentSuggestion?.panditId) return null;
    return providerCandidates.find((item) => Number(item.userId || item.id) === Number(frequentSuggestion.panditId)) || null;
  }, [frequentSuggestion, providerCandidates]);

  const serviceKeywords = useMemo(() => {
    const source = `${serviceData.title || ''} ${serviceData.name || ''} ${serviceData.description || ''}`;
    return normalizeText(source)
      .split(/[^a-z0-9]+/)
      .filter((word) => word.length > 2);
  }, [serviceData.title, serviceData.name, serviceData.description]);

  const rankedProviders = useMemo(() => {
    const recommendedId = Number(frequentSuggestion?.panditId || 0) || null;
    const normalizedCityFilter = normalizeText(cityFilter).trim();

    const providers = providerCandidates
      .map((pandit) => {
        const providerId = getProviderId(pandit);
        const specialtiesText = normalizeText((pandit.specialties || []).join(' '));
        const serviceHits = serviceKeywords.reduce((count, keyword) => {
          return count + (specialtiesText.includes(keyword) ? 1 : 0);
        }, 0);

        const serviceMatchScore = Math.min(serviceHits * 10, 30);
        const ratingScore = Number(pandit.rating || 0) * 8;
        const experienceScore = Math.min(Number(pandit.experienceYears || 0), 20);
        const recommendationScore = recommendedId && providerId === recommendedId ? 30 : 0;
        const isAvailable = providerId ? availabilityActorIds.has(String(providerId)) : false;
        const availabilityScore = isAvailable ? 12 : 0;
        const score = serviceMatchScore + ratingScore + experienceScore + recommendationScore + availabilityScore;

        return {
          ...pandit,
          providerId,
          score,
          serviceHits,
          isAvailable
        };
      })
      .filter((pandit) => {
        const matchesCity = !normalizedCityFilter || normalizeText(pandit.location).includes(normalizedCityFilter);
        const matchesAvailability = !onlyAvailable || pandit.isAvailable;
        return matchesCity && matchesAvailability;
      });

    const sorted = [...providers];
    if (sortBy === 'rating') {
      sorted.sort((a, b) => Number(b.rating || 0) - Number(a.rating || 0));
    } else if (sortBy === 'experience') {
      sorted.sort((a, b) => Number(b.experienceYears || 0) - Number(a.experienceYears || 0));
    } else {
      sorted.sort((a, b) => b.score - a.score);
    }

    return sorted;
  }, [providerCandidates, frequentSuggestion, serviceKeywords, availabilityActorIds, cityFilter, onlyAvailable, sortBy]);

  useEffect(() => {
    let cancelled = false;

    const loadPandits = async () => {
      setCatalogLoading(true);
      try {
        const data = await catalogService.getPandits();
        if (!cancelled && data.length > 0) {
          setCatalogPandits(data);
        }
      } catch (err) {
      } finally {
        if (!cancelled) {
          setCatalogLoading(false);
        }
      }
    };

    loadPandits();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadAvailability = async () => {
      if (!authService.isAuthenticated()) {
        setAvailabilityActorIds(new Set());
        return;
      }

      setAvailabilityLoading(true);
      try {
        const from = new Date();
        const to = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
        const slots = await availabilityService.searchSlots({
          actorRole: 'pandit',
          from: from.toISOString(),
          to: to.toISOString()
        });

        if (cancelled) {
          return;
        }

        const ids = new Set(
          (slots || [])
            .map((slot) => String(slot.actor_id || slot.actorId || ''))
            .filter(Boolean)
        );
        setAvailabilityActorIds(ids);
      } catch (err) {
      } finally {
        if (!cancelled) {
          setAvailabilityLoading(false);
        }
      }
    };

    loadAvailability();

    return () => {
      cancelled = true;
    };
  }, []);

  const basePrice = useMemo(() => toNumberPrice(serviceData.price), [serviceData.price]);

  const selectedDayPanditRule = useMemo(() => {
    if (!schedulePricing?.panditCountByDays) {
      return null;
    }

    const dayKey = String(selectedDurationDays);
    return schedulePricing.panditCountByDays[dayKey] || null;
  }, [schedulePricing, selectedDurationDays]);

  const panditCountOptions = useMemo(() => {
    if (!selectedDayPanditRule) {
      return [];
    }

    const min = Number(selectedDayPanditRule.min || 1);
    const max = Number(selectedDayPanditRule.max || min);
    const options = [];
    for (let value = min; value <= max; value += 1) {
      options.push(value);
    }
    return options;
  }, [selectedDayPanditRule]);

  const selectedJaapOption = useMemo(() => {
    if (!jaapPricing?.options?.length) {
      return null;
    }

    return jaapPricing.options.find((item) => Number(item.count) === Number(selectedJaapCount)) || jaapPricing.options[0];
  }, [jaapPricing, selectedJaapCount]);

  const selectedAnusthanOption = useMemo(() => {
    if (!anusthanPricing?.types?.length) {
      return null;
    }

    return anusthanPricing.types.find((item) => item.key === selectedAnusthanType) || anusthanPricing.types[0];
  }, [anusthanPricing, selectedAnusthanType]);

  const anusthanPanditOptions = useMemo(() => {
    if (!selectedAnusthanOption) {
      return [];
    }

    const options = [];
    for (let value = Number(selectedAnusthanOption.minPandits); value <= Number(selectedAnusthanOption.maxPandits); value += 1) {
      options.push(value);
    }
    return options;
  }, [selectedAnusthanOption]);

  useEffect(() => {
    if (jaapPricing?.options?.length) {
      const defaultCount = Number(jaapPricing.defaultCount || jaapPricing.options[0].count);
      setSelectedJaapCount(defaultCount);
      return;
    }

    setSelectedJaapCount(0);
  }, [jaapPricing, serviceData.id, serviceData.title]);

  useEffect(() => {
    if (anusthanPricing?.types?.length) {
      const defaultType = anusthanPricing.defaultType || anusthanPricing.types[0].key;
      setSelectedAnusthanType(defaultType);
      return;
    }

    setSelectedAnusthanType('');
  }, [anusthanPricing, serviceData.id, serviceData.title]);

  useEffect(() => {
    if (!selectedAnusthanOption) {
      return;
    }

    setSelectedPanditCount(Number(selectedAnusthanOption.defaultPandits || selectedAnusthanOption.minPandits || 1));
  }, [selectedAnusthanOption]);

  useEffect(() => {
    if (schedulePricing?.dayOptions?.length) {
      const defaultDays = Number(schedulePricing.defaultDays || schedulePricing.dayOptions[0] || 1);
      setSelectedDurationDays(defaultDays);
      return;
    }

    setSelectedDurationDays(1);
  }, [schedulePricing, serviceData.id, serviceData.title]);

  useEffect(() => {
    if (!selectedDayPanditRule) {
      setSelectedPanditCount(1);
      return;
    }

    const defaultPandits = Number(selectedDayPanditRule.defaultValue || selectedDayPanditRule.min || 1);
    setSelectedPanditCount(defaultPandits);
  }, [selectedDayPanditRule, serviceData.id, serviceData.title]);

  useEffect(() => {
    if (servicePricingConfig?.dakshina?.defaultValue) {
      setDakshinaAmount(servicePricingConfig.dakshina.defaultValue);
      return;
    }

    setDakshinaAmount(0);
  }, [servicePricingConfig, serviceData.id, serviceData.title]);

  const packages = useMemo(() => {
    if (jaapPricing?.mode === 'jaap-count' && selectedJaapOption) {
      return {
        basic: {
          name: servicePricingConfig.packageLabel || 'Jaap Charges',
          price: Number(selectedJaapOption.price || 0),
          duration: selectedJaapOption.duration || serviceData.duration,
          features: serviceData.features,
          description: `${selectedJaapOption.label} with ${selectedJaapOption.pandits}`
        }
      };
    }

    if (anusthanPricing?.mode === 'anusthan-pandit' && selectedAnusthanOption) {
      const perPanditRate = Number(selectedAnusthanOption.basePricePerPandit || 0);
      const computedPrice = perPanditRate * Number(selectedPanditCount || 1);
      return {
        basic: {
          name: servicePricingConfig.packageLabel || 'Anusthan Charges',
          price: computedPrice,
          duration: selectedAnusthanOption.duration || serviceData.duration,
          features: serviceData.features,
          description: `${selectedAnusthanOption.label} x ${selectedPanditCount} pandit(s) x ₹${perPanditRate.toLocaleString('en-IN')}/pandit`
        }
      };
    }

    if (schedulePricing?.mode === 'per-pandit-per-day') {
      const rate = Number(schedulePricing.ratePerPanditPerDay || 0);
      const computedPrice = rate * Number(selectedDurationDays || 1) * Number(selectedPanditCount || 1);
      return {
        basic: {
          name: servicePricingConfig.packageLabel || 'Ritual Charges',
          price: computedPrice,
          duration: `${selectedDurationDays} day(s)`,
          features: serviceData.features,
          description: `${selectedPanditCount} pandit(s) x ${selectedDurationDays} day(s) x ₹${rate.toLocaleString('en-IN')} per pandit/day`
        }
      };
    }

    if (servicePricingConfig?.dakshina) {
      return {
        basic: {
          name: servicePricingConfig.packageLabel || 'Ritual Dakshina',
          price: dakshinaAmount || servicePricingConfig.dakshina.defaultValue,
          duration: serviceData.duration,
          features: serviceData.features,
          description: servicePricingConfig.packageDescription || 'Flexible dakshina pricing option.'
        }
      };
    }

    const basicPrice = basePrice;
    const premiumPrice = roundToNearestHundred(basePrice * 1.35);
    const luxuryPrice = roundToNearestHundred(basePrice * 1.75);

    return {
      basic: {
        name: 'Basic Package',
        price: basicPrice,
        duration: serviceData.duration,
        features: serviceData.features,
        description: `Core ${serviceData.title} rituals with experienced pandit support`
      },
      premium: {
        name: serviceProfile.premiumLabel,
        price: premiumPrice,
        duration: serviceData.duration,
        features: [...serviceData.features, 'Priority slot scheduling', 'Expanded ceremony guidance', 'Family ritual coordination'],
        description: `${serviceData.title} with richer support and expanded ritual coverage`
      },
      luxury: {
        name: serviceProfile.luxuryLabel,
        price: luxuryPrice,
        duration: serviceData.duration,
        features: [...serviceData.features, 'Dedicated coordinator', 'High-touch ceremony planning', 'Post-ritual guidance summary', 'Priority pandit matching'],
        description: `Most comprehensive ${serviceData.title} experience with premium assistance`
      }
    };
  }, [
    basePrice,
    serviceData.duration,
    serviceData.features,
    serviceData.title,
    serviceProfile.luxuryLabel,
    serviceProfile.premiumLabel,
    servicePricingConfig,
    dakshinaAmount,
    schedulePricing,
    jaapPricing,
    anusthanPricing,
    selectedJaapOption,
    selectedAnusthanOption,
    selectedDurationDays,
    selectedPanditCount
  ]);

  const addOnServices = useMemo(() => {
    if (servicePricingConfig?.addOns?.length) {
      return servicePricingConfig.addOns;
    }

    return serviceProfile.addOns.map((item) => ({
      id: item.id,
      name: item.name,
      description: item.description,
      price: Math.max(item.min, roundToNearestHundred(basePrice * item.factor))
    }));
  }, [serviceProfile.addOns, basePrice, servicePricingConfig]);

  const serviceReviews = useMemo(() => {
    const displayName = serviceData.title || 'this service';
    return [
      {
        name: 'Priya Sharma',
        rating: 5,
        comment: `Excellent ${displayName} experience. The process was smooth and rituals were explained clearly to our family.`,
        date: '2 weeks ago'
      },
      {
        name: 'Rajesh Kumar',
        rating: 5,
        comment: `Booked ${displayName} and everything was on time. The pandit was professional and very knowledgeable.`,
        date: '1 month ago'
      },
      {
        name: 'Meera Patel',
        rating: 4,
        comment: `Great support for ${displayName}. Add-on options were useful and pricing was transparent.`,
        date: '3 weeks ago'
      }
    ];
  }, [serviceData.title]);

  const serviceFaqs = useMemo(() => {
    const displayName = serviceData.title || 'this service';
    return [
      {
        question: `How early should I book ${displayName}?`,
        answer: 'Booking 1-2 weeks in advance is ideal. For festival periods or weekends, earlier is recommended.'
      },
      {
        question: `What is included in the ${displayName} package?`,
        answer: 'Core rituals, pandit guidance, and standard ceremony flow are included. You can add custom options from Add-ons.'
      },
      {
        question: `Can I customize ${displayName} rituals based on family tradition?`,
        answer: 'Yes. During booking you can share preferences, regional customs, and timing requirements.'
      },
      {
        question: 'What happens if my schedule changes?',
        answer: 'Rescheduling support is available based on slot and pandit availability. Early notice improves flexibility.'
      }
    ];
  }, [serviceData.title]);

  useEffect(() => {
    setSelectedPackage('basic');
    setSelectedAddOns([]);
  }, [serviceData.id, serviceData.title]);

  const selectedPackageData = packages[selectedPackage];

  const handleAddOnToggle = (addOnId) => {
    setSelectedAddOns((prev) =>
      prev.includes(addOnId)
        ? prev.filter((id) => id !== addOnId)
        : [...prev, addOnId]
    );
  };

  const calculateTotalPrice = () => {
    const packagePrice = Number(selectedPackageData?.price || 0);
    const addOnPrice = selectedAddOns.reduce((total, addOnId) => {
      const addOn = addOnServices.find((item) => item.id === addOnId);
      return total + (addOn ? addOn.price : 0);
    }, 0);
    const platformFee = Number(servicePricingConfig?.platformFee || 0);
    return packagePrice + addOnPrice + platformFee;
  };

  const pricingBreakdown = useMemo(() => {
    const selectedAddOnTotal = selectedAddOns.reduce((total, addOnId) => {
      const addOn = addOnServices.find((item) => item.id === addOnId);
      return total + (addOn ? addOn.price : 0);
    }, 0);

    const packagePrice = Number(selectedPackageData?.price || 0);
    const platformFee = Number(servicePricingConfig?.platformFee || 0);
    let baseLabel = servicePricingConfig?.dakshina ? 'Dakshina' : 'Package Price';
    if (schedulePricing?.mode === 'per-pandit-per-day') {
      baseLabel = 'Ritual Charges';
    }
    if (jaapPricing?.mode === 'jaap-count') {
      baseLabel = 'Jaap Charges';
    }
    if (anusthanPricing?.mode === 'anusthan-pandit') {
      baseLabel = 'Anusthan Charges';
    }

    return {
      baseLabel,
      dakshina: packagePrice,
      platformFee,
      addOns: selectedAddOnTotal,
      total: packagePrice + selectedAddOnTotal + platformFee,
      schedule: schedulePricing?.mode === 'per-pandit-per-day'
        ? {
          days: selectedDurationDays,
          pandits: selectedPanditCount,
          ratePerPanditPerDay: Number(schedulePricing.ratePerPanditPerDay || 0)
        }
        : null,
      jaapSelection: jaapPricing?.mode === 'jaap-count' && selectedJaapOption
        ? {
          count: selectedJaapOption.count,
          label: selectedJaapOption.label,
          pandits: selectedJaapOption.pandits,
          duration: selectedJaapOption.duration
        }
        : null,
      anusthanSelection: anusthanPricing?.mode === 'anusthan-pandit' && selectedAnusthanOption
        ? {
          type: selectedAnusthanOption.label,
          pandits: selectedPanditCount,
          ratePerPandit: Number(selectedAnusthanOption.basePricePerPandit || 0),
          duration: selectedAnusthanOption.duration
        }
        : null
    };
  }, [
    selectedAddOns,
    addOnServices,
    selectedPackageData,
    servicePricingConfig,
    schedulePricing,
    jaapPricing,
    anusthanPricing,
    selectedDurationDays,
    selectedPanditCount,
    selectedJaapOption,
    selectedAnusthanOption
  ]);

  const serviceExecutionDetails = useMemo(() => {
    if (schedulePricing?.mode === 'per-pandit-per-day') {
      return {
        pandits: `${selectedPanditCount} pandit(s)`,
        duration: `${selectedDurationDays} day(s)`
      };
    }

    if (jaapPricing?.mode === 'jaap-count' && selectedJaapOption) {
      return {
        pandits: selectedJaapOption.pandits,
        duration: selectedJaapOption.duration
      };
    }

    if (anusthanPricing?.mode === 'anusthan-pandit' && selectedAnusthanOption) {
      return {
        pandits: `${selectedPanditCount} pandit(s)`,
        duration: selectedAnusthanOption.duration
      };
    }

    if (servicePricingConfig?.execution) {
      return servicePricingConfig.execution;
    }

    return {
      pandits: serviceData.panditsRequired || '1 pandit',
      duration: serviceData.estimatedCompletion || serviceData.duration || 'Depends on ceremony'
    };
  }, [schedulePricing, jaapPricing, anusthanPricing, selectedJaapOption, selectedAnusthanOption, selectedPanditCount, selectedDurationDays, servicePricingConfig, serviceData.panditsRequired, serviceData.estimatedCompletion, serviceData.duration]);

  const formatPrice = (price) => `₹${price.toLocaleString('en-IN')}`;

  const handleBookNow = () => {
    const servicePage = resolveServicePage(serviceData);
    const bookingData = {
      service: serviceData,
      package: selectedPackageData,
      addOns: selectedAddOns.map((id) => addOnServices.find((item) => item.id === id)),
      totalPrice: calculateTotalPrice(),
      pricing: {
        ...pricingBreakdown,
        platformFeeNote: servicePricingConfig?.platformFeeNote || ''
      },
      bookingMode: 'service',
      servicePage
    };

    if (onNavigate) {
      onNavigate('booking', { bookingData, servicePage });
    }
  };

  const handleBookWithPandit = (pandit) => {
    const providerId = getProviderId(pandit);
    const servicePage = resolveServicePage(serviceData);

    const bookingData = {
      service: {
        ...serviceData,
        panditId: providerId,
        actorRole: 'pandit',
        selectedPandit: pandit
      },
      package: selectedPackageData,
      addOns: selectedAddOns.map((id) => addOnServices.find((item) => item.id === id)),
      totalPrice: calculateTotalPrice(),
      pricing: {
        ...pricingBreakdown,
        platformFeeNote: servicePricingConfig?.platformFeeNote || ''
      },
      bookingMode: 'pandit',
      servicePage
    };

    if (onNavigate) {
      onNavigate('booking', { bookingData, servicePage });
    }
  };

  const handleViewPanditProfile = (pandit) => {
    if (onNavigate) {
      onNavigate('pandit-profile', { pandit });
    }
  };

  return {
    serviceData,
    parentPage,
    parentLabel,
    servicePricingConfig,
    schedulePricing,
    jaapPricing,
    anusthanPricing,
    selectedDurationDays,
    setSelectedDurationDays,
    selectedPanditCount,
    setSelectedPanditCount,
    panditCountOptions,
    selectedJaapCount,
    setSelectedJaapCount,
    selectedJaapOption,
    selectedAnusthanType,
    setSelectedAnusthanType,
    selectedAnusthanOption,
    anusthanPanditOptions,
    detailedInfo: servicePricingConfig?.detailedInfo || null,
    serviceExecutionDetails,
    serviceProfile,
    selectedPackage,
    setSelectedPackage,
    dakshinaAmount,
    setDakshinaAmount,
    selectedAddOns,
    handleAddOnToggle,
    addOnServices,
    selectedPackageData,
    formatPrice,
    calculateTotalPrice,
    pricingBreakdown,
    frequentSuggestion,
    suggestedPandit,
    rankedProviders,
    catalogLoading,
    availabilityLoading,
    cityFilter,
    setCityFilter,
    onlyAvailable,
    setOnlyAvailable,
    sortBy,
    setSortBy,
    packages,
    serviceReviews,
    serviceFaqs,
    handleBookNow,
    handleBookWithPandit,
    handleViewPanditProfile
  };
};

export default useServiceDetailsModel;
